import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, 
  ChevronDown, 
  MessageSquare, 
  ShieldAlert, 
  Server, 
  CreditCard, 
  Users, 
  Zap,
  Phone
} from 'lucide-react';

const FAQSlide = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "Are there any other platforms that offer your entire suite of 15+ AI products in one place?",
      answer: "No. Asha is the world's first and only all-in-one AI Operating System for dental. While others offer fragmented point solutions, we are the only platform to unify Front Office, Back Office, and Clinical AI into a single, seamless ecosystem.",
      icon: Server
    },
    {
      question: "Can I purchase just the Front Office or Back Office bundle separately?",
      answer: "No. We partner with practices as a complete Operating System. Our agents are designed to communicate and collaborate across departments—separating them breaks the \"brain\" of your practice. To deliver the results we guarantee, the system must run as a unified whole.",
      icon: Users
    },
    {
      question: "Do I have to migrate my current communication tools (VoIP, Messaging, Forms)?",
      answer: "Yes. Legacy tools like Weave or RevenueWell block AI from doing its job effectively. Our Communication Suite is AI-native—meaning our agents can listen, read, and transact directly within your phone and messaging lines. This migration is mandatory to unlock the full automation potential of Asha.",
      icon: Phone
    },
    {
      question: "What makes your AI technology superior to competitors?",
      answer: "We build our AI in-house, led by experts from Google AI and top health systems. Most dental competitors simply \"white-label\" or resell generic AI models they didn't build and can't fix. You wouldn't trust a general contractor to perform cardiac surgery; don't trust a legacy software company to build your practice's artificial intelligence.",
      icon: Zap
    },
    {
      question: "Can we exclude certain collections from the RCM fee (e.g., OTC payments)?",
      answer: "Generally, our model covers all collections to ensure our incentives are aligned with your total growth. However, for high-ticket cases (e.g., treatments over $10,000 paid at point of sale), we can discuss specific exclusions during the contract phase.",
      icon: CreditCard
    },
    {
      question: "Can I speak with an existing customer for a reference?",
      answer: "We respect the privacy and time of our doctor partners. Just as you wouldn't want us asking you to take reference calls for 10 prospects a week, we protect our current clients from that burden. We provide detailed case studies and data to demonstrate our impact, but we do not offer direct peer references as a standard policy.",
      icon: ShieldAlert
    },
    {
      question: "Do you offer discounts for individual practices?",
      answer: "Our pricing is already structured to be significantly lower than the cost of stitching together multiple point solutions. As such, we do not discount individual practice contracts. Custom enterprise pricing is available only for groups with 20+ locations.",
      icon: MessageSquare
    }
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-8 lg:p-12 font-sans overflow-hidden relative">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4 pointer-events-none" />

        {/* Header */}
        <div className="text-center mb-8 relative z-10 flex-shrink-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wide mb-4 border border-blue-100">
                <HelpCircle className="w-3 h-3" />
                <span>Common Questions</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                Frequently Asked <span className="text-blue-600">Questions</span>
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
                Everything you need to know about partnering with Asha.
            </p>
        </div>

        {/* FAQ Grid/List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar relative z-10 -mr-4 pr-4">
            <div className="max-w-4xl mx-auto space-y-4 pb-8">
                {faqs.map((faq, index) => (
                    <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`
                            bg-white rounded-xl border transition-all duration-300 overflow-hidden
                            ${openIndex === index 
                                ? 'border-blue-200 shadow-lg shadow-blue-500/10 ring-1 ring-blue-100' 
                                : 'border-slate-200 hover:border-blue-200 hover:shadow-md'
                            }
                        `}
                    >
                        <button
                            onClick={() => setOpenIndex(openIndex === index ? null : index)}
                            className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`
                                    w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300
                                    ${openIndex === index ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600'}
                                `}>
                                    <faq.icon className="w-5 h-5" />
                                </div>
                                <span className={`text-lg font-bold transition-colors duration-300 ${openIndex === index ? 'text-blue-900' : 'text-slate-800'}`}>
                                    {faq.question}
                                </span>
                            </div>
                            <ChevronDown 
                                className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${openIndex === index ? 'rotate-180 text-blue-600' : ''}`} 
                            />
                        </button>
                        
                        <AnimatePresence>
                            {openIndex === index && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                >
                                    <div className="px-6 pb-6 pl-[4.5rem]">
                                        <p className="text-slate-600 leading-relaxed text-base">
                                            {faq.answer}
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                ))}
            </div>
        </div>
    </div>
  );
};

export default FAQSlide;
